import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { IActivityWithTreatmentCode } from '../../../../../Activity/interface/IActivityWithTreatmentCode'
import { useAppDispatch } from '../../../../../App/Hooks/reduxHooks'
import { IUser } from '../../../../../User/DataStore/IUser'
import { getUsers } from '../../../../../User/Store/actions'
import {
  IHIInvoiceLine,
  IHIInvoiceLineActivity,
  IHIInvoiceLinePrice,
} from '../../../../interfaces/IHealthInsurance'
import { ActivityOptionFilter } from '../ActivityOptionFilter/ActivityOptionFilter'

import { DeleteButton, LineTd, TdInput, TdSelect } from './style'

type Props = {
  users: IUser[]
  activityList: IActivityWithTreatmentCode[]
  onDelete: (index: number) => void
  index: number
}

export const TableRow = ({
  activityList = [],
  users,
  onDelete,
  index,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<{
    lines: IHIInvoiceLine[]
  }>()

  useEffect(() => {
    if (users.length < 1) {
      dispatch(getUsers())
    }
  }, [])

  const activityListMemo = useMemo(
    () =>
      activityList.map(activity => ({
        treatmentName: activity.name,
        treatmentUuid: activity.uuid,
        treatmentCode: activity.treatmentCode,
        unitPrice: activity.unitPrice,
        vatPercentage: activity.vatPercentage,
      })),
    [],
  )

  const rowErrors = useMemo(
    () => (Object.keys(errors).length !== 0 ? errors.lines[index] : {}),
    [errors],
  )

  const handleDeleteLine = () => onDelete(index)

  const selectTreatment = (e: IHIInvoiceLineActivity) => {
    const currentLine = getValues().lines[index]
    const unitPriceExcl = (e.unitPrice * 100) / (100 + e.vatPercentage)
    const lineTotal =
      (e.unitPrice * currentLine.amount * (100 - currentLine.discount)) / 100
    setValue(`lines.${index}.treatmentName`, e.treatmentName)
    setValue(`lines.${index}.treatmentCode`, e.treatmentCode)
    setValue(`lines.${index}.unitPrice`, e.unitPrice)
    setValue(`lines.${index}.vatPercentage`, e.vatPercentage)
    setValue(
      `lines.${index}.unitPriceExcl`,
      Math.round((unitPriceExcl + Number.EPSILON) * 100) / 100,
    )
    setValue(
      `lines.${index}.lineTotal`,
      Math.round((lineTotal + Number.EPSILON) * 100) / 100,
    )
  }

  const handleLineTotalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentLine = getValues().lines[index]

    const targetName = e.currentTarget.name
    const simpleName = targetName.split('.')[2] as keyof IHIInvoiceLinePrice
    const targetValue = e.currentTarget.value

    switch (simpleName) {
      case 'lineTotal': {
        // Calculate the unit price
        const unitPrice: number =
          Number(targetValue) /
          (((100 - currentLine.discount) * currentLine.amount) / 100)
        // Calculate the price before VAT
        const unitPriceExcl: number =
          (unitPrice * 100) / (100 + currentLine.vatPercentage)
        setValue(
          `lines.${index}.unitPrice`,
          Math.round((unitPrice + Number.EPSILON) * 100) / 100,
        )
        setValue(
          `lines.${index}.unitPriceExcl`,
          Math.round((unitPriceExcl + Number.EPSILON) * 100) / 100,
        )
        break
      }
      default: {
        currentLine[simpleName] = Number(targetValue)
        // Calculate the unit price BEFORE VAT
        const unitPriceExcl =
          (currentLine.unitPrice * 100) / (100 + currentLine.vatPercentage)
        // Calculate the new total price
        const lineTotal =
          (currentLine.unitPrice *
            currentLine.amount *
            (100 - currentLine.discount)) /
          100
        setValue(
          `lines.${index}.lineTotal`,
          Math.round((lineTotal + Number.EPSILON) * 100) / 100,
        )
        setValue(
          `lines.${index}.unitPriceExcl`,
          Math.round((unitPriceExcl + Number.EPSILON) * 100) / 100,
        )
        break
      }
    }
    setValue(`lines.${index}.${simpleName}`, Number(targetValue))
  }
  return (
    <tr>
      <LineTd error={rowErrors && !!rowErrors.treatmentName}>
        <Controller
          name={`lines.${index}.treatmentName`}
          control={control}
          render={({ field }) => (
            <ActivityOptionFilter
              options={activityListMemo}
              onSelect={e => selectTreatment(e)}
              {...field}
            />
          )}
        />
      </LineTd>
      <LineTd error={rowErrors && !!rowErrors.treatmentCode}>
        <Controller
          name={`lines.${index}.treatmentCode`}
          control={control}
          render={({ field }) => (
            <TdInput type="text" placeholder={t('Treatment code')} {...field} />
          )}
        />
      </LineTd>
      <LineTd error={rowErrors && !!rowErrors.treatmentDate}>
        <Controller
          name={`lines.${index}.treatmentDate`}
          control={control}
          render={({ field }) => (
            <TdInput type="date" placeholder={t('Treatment date')} {...field} />
          )}
        />
      </LineTd>
      <LineTd>
        <Controller
          name={`lines.${index}.personUuid`}
          control={control}
          render={({ field }) => (
            <TdSelect
              error={rowErrors && !!rowErrors.personUuid}
              {...field}
              onChange={e => {
                const userDetails = users.find(
                  user => user.uuid === e.target.value,
                )
                if (!userDetails) return
                setValue(`lines.${index}.personName`, userDetails.firstName)
                setValue(
                  `lines.${index}.personAgbCode`,
                  userDetails.agbCode || '',
                )
                field.onChange(e)
              }}>
              <option disabled selected={!field.value}>
                {t('Select an option')}
              </option>
              {users.map(employee => (
                <option
                  key={employee.uuid}
                  value={employee.uuid}
                  selected={field.value === employee.uuid}>
                  {employee.firstName}
                </option>
              ))}
            </TdSelect>
          )}
        />
      </LineTd>
      <LineTd error={rowErrors && !!rowErrors.personAgbCode}>
        <Controller
          name={`lines.${index}.personAgbCode`}
          control={control}
          render={({ field }) => <TdInput type="text" {...field} />}
        />
      </LineTd>
      <LineTd numberCell error={rowErrors && !!rowErrors.amount}>
        <Controller
          name={`lines.${index}.amount`}
          control={control}
          render={({ field }) => (
            <TdInput
              type="number"
              {...field}
              onChange={e => handleLineTotalChange(e)}
            />
          )}
        />
      </LineTd>
      <LineTd numberCell error={rowErrors && !!rowErrors.unitPrice}>
        <Controller
          name={`lines.${index}.unitPrice`}
          control={control}
          render={({ field }) => (
            <TdInput
              type="number"
              {...field}
              onChange={handleLineTotalChange}
            />
          )}
        />
      </LineTd>
      <LineTd numberCell error={rowErrors && !!rowErrors.unitPriceExcl}>
        <Controller
          name={`lines.${index}.unitPriceExcl`}
          control={control}
          render={({ field }) => <TdInput type="number" {...field} disabled />}
        />
      </LineTd>
      <LineTd numberCell error={rowErrors && !!rowErrors.vatPercentage}>
        <Controller
          name={`lines.${index}.vatPercentage`}
          control={control}
          render={({ field }) => (
            <TdInput
              type="number"
              {...field}
              onChange={handleLineTotalChange}
            />
          )}
        />
      </LineTd>
      <LineTd numberCell error={rowErrors && !!rowErrors.discount}>
        <Controller
          name={`lines.${index}.discount`}
          control={control}
          render={({ field }) => (
            <TdInput
              type="number"
              {...field}
              onChange={handleLineTotalChange}
            />
          )}
        />
      </LineTd>
      <LineTd numberCell error={rowErrors && !!rowErrors.lineTotal}>
        <Controller
          name={`lines.${index}.lineTotal`}
          render={({ field }) => (
            <TdInput
              type="number"
              {...field}
              onChange={handleLineTotalChange}
            />
          )}
        />
      </LineTd>
      <td>
        <DeleteButton onClick={handleDeleteLine}>
          <FontAwesomeIcon icon={faTrashAlt} />
        </DeleteButton>
      </td>
    </tr>
  )
}
